import { status_configurator } from 'src/helpers/device_data_helpers';
import { isPill, isWeatherStation } from 'src/helpers/interface_helpers';
import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
import type { Device } from 'src/types/Device';

/**
 * Creates a label list from status_configurator output by filtering out main labels and state.
 * This is the common pattern used across all Gen1 Main tabs.
 *
 * When a device is passed and the user has customized their card (statusLabelParam),
 * the returned list honors the saved order and visibility so the side panel / drawer
 * matches the order shown on the device card.
 *
 * @param statuses - Output from status_configurator
 * @param device - Optional device (used to apply user-saved order/visibility)
 * @returns Filtered array of labels (excluding main and state)
 */
export function createGen1StatusLabels(statuses: any[], device?: Device): any[] {
	if (!Array.isArray(statuses)) return [];

	const defaultList = statuses.filter(
		(entry) =>
			'undefined' === typeof entry.main &&
			entry.name !== 'state' &&
			typeof entry.peripheralType === 'undefined',
	);

	if (device?.statusLabelParam) {
		return getCustomizeLabelList(device, statuses);
	}

	return defaultList;
}

/**
 * Combines timer labels with regular label list.
 * Timer labels are placed first, followed by regular labels.
 *
 * @param timerLabels - Object with timer labels (from TimerRemaining component)
 * @param labelList - Regular label list from createStatusLabels
 * @returns Combined array of labels
 */
export function createGen1CombinedLabelList(timerLabels: Record<string, any>, labelList: any[]): any[] {
	return [...Object.values(timerLabels), ...labelList];
}

/**
 * Extracts the main label from status_configurator output.
 * Main label is identified by having a 'main' property or name === 'state'.
 *
 * @param statuses - Output from status_configurator
 * @returns Main label object or undefined
 */
export function extractGen1MainLabel(statuses: any[]): any | undefined {
	return Array.isArray(statuses)
		? statuses.find((e) => 'undefined' !== typeof e.main || e.name === 'state')
		: undefined;
}

/**
 * Extracts the power status from status_configurator output.
 * Used by PM (power monitoring) devices.
 *
 * @param statuses - Output from status_configurator
 * @returns Power status object or undefined
 */
export function extractGen1PowerStatus(statuses: any[]): any | undefined {
	return Array.isArray(statuses) ? statuses.find((entry) => entry.name === 'power') : undefined;
}

/**
 * Determines if Gen1 timer is active for the device.
 * Used in beforeUpdate to set gen1Timer flag.
 *
 * @param device - Device object
 * @param status - Device status
 * @param isGen2 - Whether device is Gen2
 * @returns true if Gen1 timer is active
 */
export function setupGen1TimerLogic(device: Device, status: any, isGen2: boolean): boolean {
	if (!device || !status?.online) return false;
	if (isGen2) return false;
	if (device?.category === 'sensor') return false;
	if (device?.category === 'emeter') {
		// For emeter devices, check relays array
		return status?.[`${device.category}s`]?.[device.channel]?.has_timer || false;
	}
	// For other devices (relay, light, etc.)
	return status?.[`${device.category}s`]?.[device.channel]?.has_timer || false;
}

/**
 * Determines if Gen1 emeter timer is active.
 * Special case for SHEM-3 and other emeter devices.
 *
 * @param device - Device object
 * @param status - Device status
 * @param isGen2 - Whether device is Gen2
 * @returns true if Gen1 emeter timer is active
 */
export function setupGen1EmeterTimerLogic(device: Device, status: any, isGen2: boolean): boolean {
	if (!device || !status?.online) return false;
	if (isGen2) return false;
	if (device?.category !== 'emeter') return false;

	return status?.[`${device.category}s`]?.[device.channel]?.has_timer || false;
}

/**
 * Determines if label list should be shown for the device.
 * Used in SHEM-3 and sensor devices to conditionally render LabelList.
 *
 * @param device - Device object
 * @param status - Device status
 * @returns true if label list should be shown
 */
export function canShowGen1LabelList(device: Device | undefined, status: any): boolean {
	if (!device) return false;
	if (!status || !status.online) return false;
	if (device.category === 'virtual_device') return false;
	if (isPeripheralDevice(device.type)) return false;
	if (device.type === 'S3XT-0S' || device.type === 'SBDI-003E') return false;
	if (device.mode === 'media_player') return false;
	if (isWeatherStation(device.type)) return false;
	if (isPill(device.type)) return false;

	return true;
}

/**
 * Gets the status_configurator output for a device.
 * Returns empty array if device is offline or deviceConfig is not available.
 * Wraps status_configurator in try-catch to prevent errors when accessing undefined properties.
 *
 * @param status - Device status
 * @param device - Device object
 * @param settings - Device settings
 * @param deviceConfig - Device configuration
 * @returns Array of status objects from status_configurator, or empty array on error
 */
export function getGen1DeviceStatuses(
	status: any,
	device: Device,
	settings: any,
	deviceConfig: any,
): any[] {
	if (!status?.online || !deviceConfig) return [];

	try {
		const result = status_configurator(status, device, settings);
		return Array.isArray(result) ? result : [];
	} catch (error) {
		console.warn('getGen1DeviceStatuses: Error in status_configurator', error, {
			deviceType: device?.type,
			deviceId: device?.id,
		});
		return [];
	}
}
